import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { AccessTab, CredentialsTab, DeployTab, InfraTab, LogsTab, UsesTab } from "./project/tabs";
import { ArchitectureTab } from "./project/Architecture";
import { DocsTab } from "./project/Docs";
import { LastUsed, StateMark, Strip, pct, useChecks, type CheckRow } from "./Checks";
import { api, can, STAGES, type Env, type Project } from "../api";
import { useMe } from "../App";
import { HealthMark, city, relative } from "../components/ui";

// The question: is this project's every copy running, and where do I reach it?

function Resources({ env }: { env: Env }) {
  const rows = [
    ...env.containers.map((c) => ({
      key: `k-${c.name}`,
      kind: "Container",
      id: c.service,
      state: c.state,
      detail: c.status,
      ok: c.state === "running" && !/unhealthy/.test(c.status),
    })),
    ...env.instances.map((i) => ({
      key: `i-${i.name}`,
      kind: "EC2 box",
      id: i.name,
      state: i.state,
      detail: [i.type, i.publicIp, i.launchedAt ? `up since ${new Date(i.launchedAt).toLocaleDateString()}` : null].filter(Boolean).join(" · "),
      ok: i.state === "running",
    })),
    ...env.clusters.map((c) => ({
      key: `c-${c.name}`,
      kind: "ECS cluster",
      id: c.name,
      state: c.status === "ACTIVE" ? (c.runningTasks ? "running" : "idle") : c.status.toLowerCase(),
      detail: `${c.services} service${c.services === 1 ? "" : "s"} · ${c.runningTasks} task${c.runningTasks === 1 ? "" : "s"} running`,
      ok: c.status === "ACTIVE" && c.runningTasks > 0,
    })),
    ...env.stacks.map((s) => ({
      key: `s-${s.stackName}`,
      kind: "Stack",
      id: s.stackName,
      state: s.status === "NOT_DEPLOYED" ? "not deployed" : s.status.replace(/_/g, " ").toLowerCase(),
      detail: s.updatedAt ? `changed ${relative(s.updatedAt)}` : "",
      ok: /_COMPLETE$/.test(s.status) && !/ROLLBACK|DELETE/.test(s.status),
    })),
  ];
  if (!rows.length) return <div className="empty">Nothing recorded behind this environment yet.</div>;
  return (
    <table>
      <thead><tr><th>Runs on</th><th>Name</th><th>State</th><th className="hide-phone">Detail</th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key}>
            <td className="muted">{r.kind}</td>
            <td className="mono">{r.id}</td>
            <td><span className={`health ${r.ok ? "up" : r.state === "stopped" || r.state === "idle" ? "stopped" : r.state === "missing" ? "down" : ""}`}><span className="dot" />{r.state}</span></td>
            <td className="hide-phone muted num">{r.detail || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** The latest check: state and why, when a person last used it, uptime. */
function CheckLine({ check }: { check: CheckRow }) {
  return (
    <div className="check-line">
      <StateMark state={check.state} />
      {check.reasons.length > 0 && <span className={check.state === "down" ? "error" : ""}>{check.reasons.join("; ")}</span>}
      <span className="muted small">Last used: <span style={{ display: "inline-flex", gap: 6 }}><LastUsedInline u={check.lastUsed} /></span></span>
      <span className="muted small num">Uptime {pct(check.uptime24h)} 24 h · {pct(check.uptime7d)} 7 d</span>
      <Strip strip={check.strip} />
    </div>
  );
}

function LastUsedInline({ u }: { u: CheckRow["lastUsed"] }) {
  if (!u.recorded || !u.lastRequestAt) return <LastUsed u={u} />;
  return <>{relative(u.lastRequestAt)} · {u.requests24h.toLocaleString()} requests in 24 h</>;
}

/** Health checks: full URLs, one per line, each with an optional =<expected status>. */
function HealthChecks({ env, slug, mayEdit }: { env: Env; slug: string; mayEdit: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(env.checks.join("\n"));
  const save = useMutation({
    mutationFn: () => api(`/projects/${slug}/envs/${env.name}/checks`, { method: "PUT", body: JSON.stringify({ checks: text.split("\n").map((l) => l.trim()).filter(Boolean) }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project", slug] });
      qc.invalidateQueries({ queryKey: ["checks"] });
      setEditing(false);
    },
  });
  if (editing) {
    return (
      <form className="field" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <span>Health checks</span>
        <textarea className="input mono" value={text} onChange={(e) => setText(e.target.value)} placeholder={"https://api.example.scdev.io/_health=204\nhttps://api.example.scdev.io/api/health"} />
        <span className="hint">One URL a line, probed every minute. Add =204 to expect that status; without one, anything below 400 passes.</span>
        {save.error && <p className="error">{save.error.message}</p>}
        <div className="actions"><button className="btn primary" disabled={save.isPending}>Save</button><button type="button" className="btn" onClick={() => { setEditing(false); setText(env.checks.join("\n")); }}>Cancel</button></div>
      </form>
    );
  }
  return (
    <div className="check-line small">
      {env.checks.length ? env.checks.map((c) => <span key={c} className="mono">{c.replace(/^https?:\/\//, "")}</span>) : <span className="muted">{env.urls.length ? "none, only the URLs are probed" : "none"}</span>}
      {mayEdit && <button className="btn ghost small" onClick={() => setEditing(true)}>Edit</button>}
    </div>
  );
}

function Environment({ env, check, slug, mayEdit }: { env: Env; check?: CheckRow; slug: string; mayEdit: boolean }) {
  return (
    <section className="env" aria-label={`${env.name} environment`}>
      <div className="env-head">
        <div className="left">
          <h3 className="mono" style={{ fontSize: 15 }}>{env.name}</h3>
          <HealthMark health={env.health} reasons={env.healthReasons} />
          {env.onLab ? <span className="pill positive">On the lab</span> : <span className="pill" title="Runs outside the lab; the lab watches what runs">Not on the lab</span>}
          {env.sharedDb && <span className="pill">Lab database</span>}
          {env.ttl && <span className="pill warning">Expires {env.ttl.slice(0, 10)}</span>}
        </div>
        <span className="muted">{env.onLab ? "Lab host" : city(env.region)} <span className="mono small">{env.region}</span></span>
      </div>
      <div className="env-body">
        {check && <CheckLine check={check} />}
        {env.purpose && <p>{env.purpose}</p>}
        {/* the same facts for every environment, on the lab or not */}
        <dl className="kv env-facts">
          <dt>URLs</dt>
          <dd>
            {env.urls.length > 0 ? (
              <div className="chips">{env.urls.map((u) => <a key={u} className="chip-link" href={u} target="_blank" rel="noreferrer">{u.replace(/^https?:\/\//, "")}</a>)}</div>
            ) : <span className="muted">none recorded, so nothing is probed</span>}
          </dd>
          <dt>Health checks</dt>
          <dd><HealthChecks env={env} slug={slug} mayEdit={mayEdit} /></dd>
          <dt>Release</dt>
          <dd>
            {env.onLab
              ? env.lastShipAt ? <>{relative(env.lastShipAt)} · <span className="mono">{env.lastShipSha?.slice(0, 8)}</span></> : <span className="muted">never shipped from the lab</span>
              : <span className="muted">deployed outside the lab; what runs is read from AWS</span>}
          </dd>
          <dt>Last used</dt>
          <dd>{check ? <LastUsedInline u={check.lastUsed} /> : <span className="muted">—</span>}{check && !check.lastUsed.recorded && !env.onLab && <span className="muted"> (only the lab's access log records use)</span>}</dd>
          <dt>Architecture</dt>
          <dd><Link to={`/p/${slug}?tab=architecture&env=${encodeURIComponent(env.name)}`} className="chip-link">{env.onLab ? "Route 53 → lab host → containers → RDS" : "DNS → AWS resources, read live"}</Link></dd>
        </dl>
      </div>
      <div className="table-wrap"><Resources env={env} /></div>
      {env.services.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Service</th><th>What it does</th><th className="hide-phone">Image</th></tr></thead>
            <tbody>
              {env.services.map((s) => (
                <tr key={s.name}>
                  <td className="cell-main mono">{s.name}</td>
                  <td>{s.description || <span className="muted">—</span>}</td>
                  <td className="hide-phone mono muted">{s.image || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function EditProject({ project, done }: { project: Project; done: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    name: project.name, description: project.description, client: project.client, repo: project.repo,
    stage: project.stage, notes: project.notes, contact: project.contact,
  });
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });
  const save = useMutation({
    mutationFn: () => api(`/projects/${project.slug}`, { method: "PATCH", body: JSON.stringify(f) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project", project.slug] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      done();
    },
  });
  return (
    <form className="panel" style={{ display: "grid", gap: 16 }} onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
      <div className="form-grid">
        <label className="field"><span>Name</span><input className="input" value={f.name} onChange={set("name")} required /></label>
        <label className="field"><span>Client</span><input className="input" value={f.client} onChange={set("client")} /></label>
        <label className="field"><span>Bitbucket repository</span><input className="input" value={f.repo} onChange={set("repo")} placeholder="525k/yms" /></label>
        <label className="field"><span>Stage</span>
          <select className="input" value={f.stage} onChange={set("stage")}>{STAGES.map((s) => <option key={s}>{s}</option>)}</select>
        </label>
        <label className="field"><span>Contact</span><input className="input" value={f.contact} onChange={set("contact")} placeholder="Who answers questions about it" /></label>
      </div>
      <label className="field"><span>Description</span><textarea className="input" value={f.description} onChange={set("description")} /></label>
      <label className="field"><span>Notes</span><textarea className="input" value={f.notes} onChange={set("notes")} /></label>
      {save.error && <p className="error">{save.error.message}</p>}
      <div className="actions"><button className="btn primary" disabled={save.isPending}>Save</button><button type="button" className="btn" onClick={done}>Cancel</button></div>
    </form>
  );
}

export function ProjectPage() {
  const { slug = "" } = useParams();
  const { data: me } = useMe();
  const [editing, setEditing] = useState(false);
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") ?? "overview";
  const q = useQuery({ queryKey: ["project", slug], queryFn: () => api<Project>(`/projects/${slug}`) });
  const checks = useChecks(slug);

  if (q.isPending) return <div className="canvas"><h1>{slug}</h1><span className="skeleton" style={{ width: 360, height: 16 }} /></div>;
  if (q.error) return <div className="canvas"><h1>{slug}</h1><p className="error">{q.error.message}</p></div>;
  const p = q.data;
  const mayEdit = me && (p.owner_id === me.id || can(me, "admin"));
  const running = p.environments.filter((e) => e.health === "up").length;

  return (
    <div className="canvas">
      <div>
        <div className="crumbs small"><Link to="/projects">Projects</Link><span>/</span><span className="mono">{p.slug}</span></div>
        <div className="title-row">
          <div>
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <h1>{p.name}</h1>
              <span className="pill">{p.stage}</span>
            </div>
            <p className="sub">
              {p.environments.length === 0 ? "No environments yet." : `${running} of ${p.environments.length} environment${p.environments.length === 1 ? "" : "s"} running`}
              {p.client ? ` · for ${p.client}` : ""}
            </p>
          </div>
          {mayEdit && !editing && <button className="btn" onClick={() => setEditing(true)}>Edit details</button>}
        </div>
      </div>

      {editing && <EditProject project={p} done={() => setEditing(false)} />}

      <div className="tabs" role="tablist" aria-label="Project">
        {[
          ["overview", "Overview"], ["infra", "Infra"], ["credentials", `Credentials${p.credentialCount ? ` ${p.credentialCount}` : ""}`],
          ["architecture", "Architecture"], ["docs", "Docs"], ["uses", "Uses"], ["logs", "Logs"], ["access", "Access"], ["deploy", "Deploy"],
        ].map(([id, label]) => (
          <button key={id} role="tab" className="tab" aria-selected={tab === id} onClick={() => setParams(id === "overview" ? {} : { tab: id ?? "" })}>{label}</button>
        ))}
      </div>

      {tab === "infra" && <InfraTab project={p} />}
      {tab === "credentials" && <CredentialsTab project={p} />}
      {tab === "architecture" && <ArchitectureTab project={p} />}
      {tab === "docs" && <DocsTab project={p} />}
      {tab === "uses" && <UsesTab project={p} />}
      {tab === "logs" && <LogsTab project={p} />}
      {tab === "access" && <AccessTab project={p} />}
      {tab === "deploy" && <DeployTab project={p} />}

      {tab === "overview" && <div className="split">
        <section aria-labelledby="envs">
          <div className="section-head"><h2 id="envs">Environments</h2></div>
          <div style={{ display: "grid", gap: 16 }}>
            {p.environments.length === 0 && <div className="empty">No environment yet. Self-service create arrives with the next release of the lab.</div>}
            {p.environments.map((e) => <Environment key={e.id} env={e} slug={p.slug} mayEdit={Boolean(mayEdit && can(me, "dev"))} check={checks.data?.rows.find((c) => c.envId === e.id)} />)}
          </div>
        </section>

        <aside className="panel" aria-labelledby="about">
          <h2 id="about" style={{ marginBottom: 12 }}>About</h2>
          <p style={{ marginBottom: 16 }}>{p.description || <span className="muted">No description.</span>}</p>
          <dl className="kv">
            <dt>Client</dt><dd>{p.client || <span className="muted">—</span>}</dd>
            <dt>Owner</dt><dd>{p.ownerName ?? p.owner ?? <span className="muted">—</span>}</dd>
            <dt>Contact</dt><dd>{p.contact || <span className="muted">—</span>}</dd>
            <dt>Repository</dt><dd>{p.repo ? <a className="mono" href={`https://bitbucket.org/${p.repo}`} target="_blank" rel="noreferrer" style={{ textDecoration: "underline", textUnderlineOffset: 3 }}>{p.repo}</a> : <span className="muted">—</span>}</dd>
            <dt>In the lab since</dt><dd className="num">{new Date(p.created_at).toLocaleDateString()}</dd>
          </dl>
          {p.tags?.length > 0 && (
            <><h3 style={{ margin: "20px 0 8px" }}>Tags</h3><div className="chips">{p.tags.map((t) => <span key={t} className="pill">{t}</span>)}</div></>
          )}
          {p.costPerMonth != null && (
            <><h3 style={{ margin: "20px 0 6px" }}>Cost today</h3><p><span className="num">${Math.round(p.costPerMonth).toLocaleString()}</span> <span className="muted">a month, Aug–Oct average · <button className="btn ghost small" onClick={() => setParams({ tab: "infra" })}>Production estimate</button></span></p></>
          )}
          {p.notes && <><h3 style={{ margin: "20px 0 6px" }}>Notes</h3><p className="muted" style={{ whiteSpace: "pre-wrap" }}>{p.notes}</p></>}
        </aside>
      </div>}
    </div>
  );
}
